import type { SessionArea } from '#imports';
// Session-timeout wiring, client-only. Sole owner of regime transitions and timers; mutates the
// search-timeout store from the route. Absolute deadlines: results 5/10 min, funnel 15/25 min.

interface RegimeTiming {
  warning: number;
  expired: number;
}

export default defineNuxtPlugin(() => {
  const store = useOfferExpirationStore();
  const route = useRoute();

  const REGIMES: Record<SessionArea, RegimeTiming> = {
    results: { warning: 5, expired: 10 },
    funnel: { warning: 15, expired: 25 },
  };

  const warning = useTimeoutFn(
    () => {
      store.markWarning();
      useGtmStore().trackOfferExpiration('warning');
    },
    () => (store.regime ? REGIMES[store.regime].warning * 60_000 : 0),
    { immediate: false },
  );
  const expiration = useTimeoutFn(
    () => {
      store.markExpired();
      useGtmStore().trackOfferExpiration('expired');
    },
    () => (store.regime ? REGIMES[store.regime].expired * 60_000 : 0),
    { immediate: false },
  );

  const start = (regime: SessionArea) => {
    store.activate(regime, REGIMES[regime].expired - REGIMES[regime].warning);
    warning.start();
    expiration.start();
  };

  const stop = () => {
    warning.stop();
    expiration.stop();
    store.$reset();
  };

  watch(
    () => route.name,
    () => {
      // The funnel zone covers every funnel page, payment included; the timer is stopped on the
      // "Pay" click (payment.vue).
      const area = currentSessionArea(route.name);
      if (!area) {
        if (store.regime) {
          stop();
        }
        return;
      }
      if (store.regime !== area) {
        start(area);
      }
    },
    { immediate: true },
  );

  return {
    provide: {
      // (Re-)arm the results timer on each successful GDS quote. Query-driven: called only from
      // results.vue (mounted only on /results, where the route watch has already set regime 'results'),
      // so no regime guard is needed here.
      resetOfferExpiration: () => {
        start('results');
      },
      // Stop timers and clear regime on the "Pay" click (payment.vue); re-entering a funnel page re-arms.
      stopOfferExpiration: () => {
        stop();
      },
    },
  };
});
